import React, { useEffect, useMemo, useState } from "react";
import {
  Container,
  Form,
  Button,
  Row,
  Col,
  Card,
  Badge,
} from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams, Link } from "react-router-dom";

import {
  createPortfolio,
  getPortfolioDetails,
  updatePortfolio,
  deletePortfolio,
} from "../../actions/portfolioActions";

import {
  PORTFOLIO_CREATE_RESET,
  PORTFOLIO_UPDATE_RESET,
  PORTFOLIO_DELETE_RESET,
  PORTFOLIO_SECTIONS,
  getPortfolioSectionLabel,
} from "../../constants/portfolioConstants";

import Loader from "../../components/Loader";
import Message from "../../components/Message";
import Uploader from "../../components/images/Uploader";

const normalizeExistingImages = (item) => {
  if (Array.isArray(item?.images) && item.images.length > 0) {
    return item.images.map((img) => ({
      url: img.url,
      public_id: img.public_id || img.url,
      alt: img.alt || "",
      caption: img.caption || "",
    }));
  }

  if (item?.image?.url) {
    return [
      {
        url: item.image.url,
        public_id: item.image.public_id || item.image.url,
        alt: item.image.alt || "",
        caption: item.image.caption || item.caption || "",
      },
    ];
  }

  return [];
};

const defaultEditPath = (itemId) => `/admin/portfolio/${itemId}`;

export default function AdminPortfolioFormScreen({
  defaultSection = "production",
  lockedSection = "",
  listPath = "/admin/portfolio",
  getEditPath = defaultEditPath,
  description = "Portfolio slider content",
}) {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const requestedSection = lockedSection || defaultSection;
  const requestedSectionTitle = getPortfolioSectionLabel(requestedSection);

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const details = useSelector((state) => state.portfolioDetails);
  const { loading: loadingDetails, error: errorDetails, item } = details;

  const created = useSelector((state) => state.portfolioCreate);
  const {
    loading: loadingCreate,
    error: errorCreate,
    success: successCreate,
    item: createdItem,
  } = created;

  const updated = useSelector((state) => state.portfolioUpdate);
  const {
    loading: loadingUpdate,
    error: errorUpdate,
    success: successUpdate,
  } = updated;

  const deleted = useSelector((state) => state.portfolioDelete);
  const {
    loading: loadingDelete,
    error: errorDelete,
    success: successDelete,
  } = deleted;

  const busy =
    loadingDetails || loadingCreate || loadingUpdate || loadingDelete;

  const [afterSave, setAfterSave] = useState("back");

  const [section, setSection] = useState(requestedSection);
  const [title, setTitle] = useState(requestedSectionTitle);
  const [subtitle, setSubtitle] = useState("");
  const [bodyText, setBodyText] = useState("");
  const [order, setOrder] = useState(1);
  const [isPublished, setIsPublished] = useState(false);

  const [images, setImages] = useState([]);

  const bodyArray = useMemo(() => {
    return bodyText
      .split("\n")
      .map((s) => s.trim())
      .filter(Boolean);
  }, [bodyText]);

  useEffect(() => {
    if (isEdit) {
      dispatch(getPortfolioDetails(id));
    } else {
      dispatch({ type: PORTFOLIO_CREATE_RESET });
      dispatch({ type: PORTFOLIO_UPDATE_RESET });
      dispatch({ type: PORTFOLIO_DELETE_RESET });

      setSection(requestedSection);
      setTitle(requestedSectionTitle);
      setSubtitle("");
      setBodyText("");
      setOrder(1);
      setIsPublished(false);
      setImages([]);
    }
  }, [dispatch, id, isEdit, requestedSection, requestedSectionTitle]);

  useEffect(() => {
    if (!isEdit || !item) return;

    setSection(item.section || requestedSection);
    setTitle(
      item.title ||
        getPortfolioSectionLabel(item.section || requestedSection),
    );
    setSubtitle(item.subtitle || "");
    setBodyText((item.body || []).join("\n\n"));
    setOrder(Number(item.order || 1));
    setIsPublished(Boolean(item.isPublished));
    setImages(normalizeExistingImages(item));
  }, [isEdit, item, requestedSection]);

  useEffect(() => {
    if (successCreate && createdItem?._id) {
      dispatch({ type: PORTFOLIO_CREATE_RESET });
      navigate(getEditPath(createdItem._id));
    }
  }, [successCreate, createdItem, dispatch, navigate, getEditPath]);

  useEffect(() => {
    if (!successUpdate) return;

    dispatch({ type: PORTFOLIO_UPDATE_RESET });

    if (afterSave === "stay" && isEdit) {
      dispatch(getPortfolioDetails(id));
      return;
    }

    navigate(listPath);
  }, [successUpdate, afterSave, dispatch, navigate, id, isEdit, listPath]);

  useEffect(() => {
    if (successDelete) {
      dispatch({ type: PORTFOLIO_DELETE_RESET });
      navigate(listPath);
    }
  }, [successDelete, dispatch, navigate, listPath]);

  const updateImageField = (index, field, value) => {
    setImages((prev) =>
      prev.map((img, idx) =>
        idx === index
          ? {
              ...img,
              [field]: value,
            }
          : img,
      ),
    );
  };

  const buildImagesPayload = () => {
    return (Array.isArray(images) ? images : [])
      .filter((im) => im?.url)
      .map((im) => ({
        url: im.url,
        public_id: im.public_id || "",
        alt: (im.alt || "").trim(),
        caption: (im.caption || "").trim(),
      }));
  };

  const submitHandler = (e) => {
    e.preventDefault();

    const imagesPayload = buildImagesPayload();

    if (imagesPayload.length === 0) {
      alert("Please upload at least one image for this slide.");
      return;
    }

    const payload = {
      section,
      title,
      subtitle,
      body: bodyArray,
      order: Number(order) || 0,
      isPublished,
      images: imagesPayload,
    };

    if (isEdit) dispatch(updatePortfolio(id, payload));
    else dispatch(createPortfolio(payload));
  };

  const deleteHandler = () => {
    if (!isEdit) return;
    if (window.confirm("Delete this slide? (soft delete)")) {
      dispatch(deletePortfolio(id));
    }
  };

  const restoreHandler = () => {
    if (!isEdit) return;
    dispatch(
      updatePortfolio(id, {
        isDeleted: false,
        isPublished: false,
      }),
    );
    setAfterSave("stay");
  };

  const duplicateHandler = () => {
    const imagesPayload = buildImagesPayload();

    if (imagesPayload.length === 0) {
      alert("Upload/select at least one image before duplicating.");
      return;
    }

    dispatch(
      createPortfolio({
        section,
        title,
        subtitle,
        body: bodyArray,
        order: Number(order) + 1,
        isPublished: false,
        images: imagesPayload,
      }),
    );
  };

  const isDeletedFlag = Boolean(item?.isDeleted);
  const sectionMismatch =
    Boolean(lockedSection) &&
    Boolean(item?.section) &&
    item.section !== lockedSection;

  return (
    <Container className="py-4">
      <Row className="align-items-center mb-3">
        <Col>
          <h1 className="mb-1">{isEdit ? "Edit Slide" : "Create Slide"}</h1>

          <div className="d-flex gap-2 align-items-center flex-wrap">
            <span className="text-muted">{description}</span>

            {isEdit && (
              <>
                <Badge bg={isDeletedFlag ? "secondary" : "dark"}>
                  {isDeletedFlag ? "Deleted" : "Active"}
                </Badge>
                <Badge bg={isPublished ? "success" : "warning"}>
                  {isPublished ? "Published" : "Unpublished"}
                </Badge>
              </>
            )}
          </div>
        </Col>

        <Col md="auto" className="d-flex gap-2 flex-wrap">
          <Link to={listPath} className="btn btn-light">
            Back
          </Link>

          {isEdit && (
            <>
              <Button variant="outline-dark" onClick={duplicateHandler}>
                Duplicate
              </Button>

              {isDeletedFlag ? (
                <Button variant="outline-success" onClick={restoreHandler}>
                  Restore
                </Button>
              ) : (
                <Button variant="outline-danger" onClick={deleteHandler}>
                  Delete
                </Button>
              )}
            </>
          )}
        </Col>
      </Row>

      {busy && <Loader />}

      {errorDetails && <Message variant="danger">{errorDetails}</Message>}
      {errorCreate && <Message variant="danger">{errorCreate}</Message>}
      {errorUpdate && <Message variant="danger">{errorUpdate}</Message>}
      {errorDelete && <Message variant="danger">{errorDelete}</Message>}
      {sectionMismatch ? (
        <Message variant="danger">
          This slide belongs to {getPortfolioSectionLabel(item.section)}, not{" "}
          {getPortfolioSectionLabel(lockedSection)}.
        </Message>
      ) : null}

      {(!isEdit || item) && !sectionMismatch && (
        <Form onSubmit={submitHandler}>
          <Row className="g-4">
            <Col lg={7}>
              <Card className="shadow-sm">
                <Card.Body>
                  <Form.Group className="mb-3">
                    <Form.Label>Section</Form.Label>
                    {lockedSection ? (
                      <Form.Control
                        plaintext
                        readOnly
                        value={getPortfolioSectionLabel(section)}
                      />
                    ) : (
                      <Form.Select
                        value={section}
                        onChange={(e) => setSection(e.target.value)}
                      >
                        {PORTFOLIO_SECTIONS.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </Form.Select>
                    )}
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>Title</Form.Label>
                    <Form.Control
                      value={title}
                      onChange={(e) => setTitle(e.target.value)}
                      required
                    />
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>Subtitle</Form.Label>
                    <Form.Control
                      value={subtitle}
                      onChange={(e) => setSubtitle(e.target.value)}
                    />
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>Body (paragraphs)</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={10}
                      value={bodyText}
                      onChange={(e) => setBodyText(e.target.value)}
                      placeholder={
                        "One paragraph per line.\n\nLeave a blank line to separate paragraphs."
                      }
                    />
                  </Form.Group>

                  <Row className="g-3">
                    <Col md={4}>
                      <Form.Group>
                        <Form.Label>Order</Form.Label>
                        <Form.Control
                          type="number"
                          value={order}
                          onChange={(e) => setOrder(e.target.value)}
                        />
                      </Form.Group>
                    </Col>

                    <Col md={4} className="d-flex align-items-end">
                      <Form.Check
                        type="checkbox"
                        label="Published"
                        checked={isPublished}
                        onChange={(e) => setIsPublished(e.target.checked)}
                      />
                    </Col>
                  </Row>

                  <div className="mt-4 d-flex gap-2 flex-wrap">
                    <Button
                      variant="dark"
                      type="submit"
                      onClick={() => setAfterSave("back")}
                    >
                      {isEdit ? "Save" : "Create"}
                    </Button>

                    {isEdit && (
                      <Button
                        variant="outline-dark"
                        type="submit"
                        onClick={() => setAfterSave("stay")}
                      >
                        Save & stay
                      </Button>
                    )}

                    <Link
                      to={listPath}
                      className="btn btn-outline-dark"
                    >
                      Cancel
                    </Link>
                  </div>
                </Card.Body>
              </Card>
            </Col>

            <Col lg={5}>
              <Card className="shadow-sm">
                <Card.Body>
                  <h5>Images</h5>

                  <Uploader
                    folder={`portfolio/${section}`}
                    existingImages={images}
                    setImageUrls={setImages}
                  />

                  {images.length > 0 && (
                    <div className="mt-4">
                      <div
                        className="text-muted mb-3"
                        style={{ fontSize: "0.9rem" }}
                      >
                        The first image is the default hero image. Each image
                        can have its own alt text and overlay text.
                      </div>

                      <div className="d-flex flex-column gap-3">
                        {images.map((img, idx) => (
                          <Card
                            key={img.public_id || img.url || idx}
                            className="border"
                          >
                            <Card.Body>
                              <div className="d-flex gap-3 align-items-start">
                                <img
                                  src={img.url}
                                  alt={img.alt || `portfolio-${idx + 1}`}
                                  style={{
                                    width: 96,
                                    height: 96,
                                    objectFit: "cover",
                                    borderRadius: 8,
                                    border: "1px solid rgba(18,18,18,0.12)",
                                    flexShrink: 0,
                                  }}
                                />

                                <div className="flex-grow-1">
                                  <div className="mb-2">
                                    <strong>Image {idx + 1}</strong>
                                    {idx === 0 && (
                                      <span className="text-muted ms-2">
                                        (Primary)
                                      </span>
                                    )}
                                  </div>

                                  <Form.Group className="mb-2">
                                    <Form.Label>Alt text</Form.Label>
                                    <Form.Control
                                      value={img.alt || ""}
                                      onChange={(e) =>
                                        updateImageField(
                                          idx,
                                          "alt",
                                          e.target.value,
                                        )
                                      }
                                      placeholder="Describe the image for accessibility"
                                    />
                                  </Form.Group>

                                  <Form.Group>
                                    <Form.Label>Overlay text</Form.Label>
                                    <Form.Control
                                      value={img.caption || ""}
                                      onChange={(e) =>
                                        updateImageField(
                                          idx,
                                          "caption",
                                          e.target.value,
                                        )
                                      }
                                      placeholder="Text shown on the image"
                                    />
                                  </Form.Group>
                                </div>
                              </div>
                            </Card.Body>
                          </Card>
                        ))}
                      </div>
                    </div>
                  )}

                  {images?.[0]?.url && (
                    <div className="mt-3">
                      <div
                        className="text-muted mb-2"
                        style={{ fontSize: "0.9rem" }}
                      >
                        Primary image preview
                      </div>

                      <img
                        src={images[0].url}
                        alt={images[0].alt || "Preview"}
                        style={{
                          width: "100%",
                          borderRadius: 12,
                          border: "1px solid rgba(18,18,18,0.12)",
                        }}
                      />
                    </div>
                  )}
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Form>
      )}
    </Container>
  );
}
